import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoArrowDownOutline } from 'react-icons/io5';

export default function AdminRentalInward() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inspectOpen, setInspectOpen] = useState(null);
  const [inspectResult, setInspectResult] = useState('ok');
  const [inspectNotes, setInspectNotes] = useState('');

  const load = () => {
    api('/api/admin/rental/inward')
      .then((data) => { setList(data); setError(''); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleRecordReturn = async (orderId) => {
    setError('');
    try {
      await api(`/api/admin/rental/inward/${orderId}`, { method: 'POST' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInspect = async () => {
    if (!inspectOpen) return;
    setError('');
    try {
      await api(`/api/admin/rental/inspect/${inspectOpen.order_item_id}`, {
        method: 'POST',
        body: JSON.stringify({ result: inspectResult, notes: inspectNotes || undefined }),
      });
      setInspectOpen(null);
      setInspectResult('ok');
      setInspectNotes('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <>
      <Card>
        <CardHeader title="Inward" subheader="Returns received or due" />
        <CardContent>
          {error && <Typography color="error" className="admin-error" sx={{ mb: 2 }}>{error}</Typography>}
          {list.length === 0 ? (
            <AdminEmptyState message="No returns in inward." icon={IoArrowDownOutline} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Event date</TableCell>
                    <TableCell>Return by</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>#{o.id}</TableCell>
                      <TableCell>{o.status?.replace(/_/g, ' ')}</TableCell>
                      <TableCell>{o.event_date}</TableCell>
                      <TableCell>{o.return_by}</TableCell>
                      <TableCell>{o.contact_name || o.contact_email || '–'}</TableCell>
                      <TableCell>
                        {o.items?.map((it) => (
                          <Box key={it.order_item_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{it.product_name} × {it.quantity}</span>
                            {it.tracking_status === 'inward' && (
                              <Button size="small" onClick={() => setInspectOpen(it)}>Inspect</Button>
                            )}
                          </Box>
                        ))}
                      </TableCell>
                      <TableCell align="right">
                        {o.status === 'delivered' && (
                          <Button size="small" variant="contained" onClick={() => handleRecordReturn(o.id)}>
                            Record return
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!inspectOpen} onClose={() => setInspectOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Inspect: {inspectOpen?.product_name}</DialogTitle>
        <DialogContent>
          <RadioGroup value={inspectResult} onChange={(e) => setInspectResult(e.target.value)}>
            <FormControlLabel value="ok" control={<Radio />} label="OK – send to laundry" />
            <FormControlLabel value="damaged" control={<Radio />} label="Damaged – hide from website" />
          </RadioGroup>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={2}
            value={inspectNotes}
            onChange={(e) => setInspectNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspectOpen(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleInspect}>Submit</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
